import { createGetQualificationsQueryOptions } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { createFetchWorkplaceWorkerTypesQueryOptions } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import { type QueryClient } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- date formatting for query key alignment
import { format } from "date-fns";

import {
  getMonthlyCalendarDateRange,
  getMonthlyCalendarDays,
} from "../MonthlyView/utils/monthlyCalendarHelpers";
import { createDayShiftsQueryOptions } from "./api/useGetDayShifts";
import { createFacilityShiftCountsQueryOptions } from "./api/useGetFacilityShiftCounts";
import { createQualificationCountsQueryOptions } from "./api/useGetQualificationCounts";
import { createInitialDateRange } from "./hooks/useDailyViewDataHelpers";

interface PrefetchScheduleDataParams {
  queryClient: QueryClient;
  facilityId: string;
  timezone?: string;
  selectedDate?: Date;
}

export async function prefetchScheduleData(params: PrefetchScheduleDataParams): Promise<void> {
  const { queryClient, facilityId, selectedDate = new Date() } = params;

  if (!facilityId) {
    return;
  }

  const timezone = params.timezone ?? "UTC";
  const dailyDateRange = createInitialDateRange(selectedDate);
  const monthlyDateRange = getMonthlyCalendarDateRange(selectedDate);

  // yyyy-MM-dd strings matching the query keys used in DailyViewContainer and MonthlyViewContainer
  const dailyDateString = format(selectedDate, "yyyy-MM-dd");
  const calendarDays = getMonthlyCalendarDays(selectedDate);
  const monthlyStart = format(calendarDays[0], "yyyy-MM-dd");
  const monthlyEnd = format(calendarDays[calendarDays.length - 1], "yyyy-MM-dd");

  await Promise.allSettled([
    queryClient.prefetchQuery(
      createDayShiftsQueryOptions({
        date: selectedDate,
        facilityId,
        timezone,
      })
    ),
    // Single unfiltered facilityCount for calendar grid dots
    queryClient.prefetchQuery(
      createFacilityShiftCountsQueryOptions({
        facilityId,
        dateFilter: dailyDateRange,
        timezone,
        enabled: true,
      })
    ),
    queryClient.prefetchQuery(
      createFacilityShiftCountsQueryOptions({
        facilityId,
        dateFilter: monthlyDateRange,
        timezone,
        enabled: true,
      })
    ),
    // Worker types and qualifications for the sidebar
    queryClient.fetchQuery(
      createFetchWorkplaceWorkerTypesQueryOptions({
        workplaceId: facilityId,
        showHidden: true,
      })
    ),
    queryClient.fetchQuery(
      createGetQualificationsQueryOptions({
        filter: {
          workplaceId: facilityId,
          enabled: true,
        },
      })
    ),
    // Single qualification-counts call per view (replaces N per-qualification facilityCount calls)
    queryClient.prefetchQuery(
      createQualificationCountsQueryOptions({
        facilityId,
        start: dailyDateString,
        end: dailyDateString,
        timezone,
      })
    ),
    queryClient.prefetchQuery(
      createQualificationCountsQueryOptions({
        facilityId,
        start: monthlyStart,
        end: monthlyEnd,
        timezone,
      })
    ),
  ]);
}
